import React, { useState, useEffect } from 'react';
import { Button, Col, Form, FormGroup, Label } from 'reactstrap';
import { change, Field, reduxForm } from 'redux-form';
import moment from 'moment';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import ValidatedInput from "../../../components/ValidatedInput";
import ValidatedSelect from "../../../components/ValidatedSelect";
import { getCountries, getImmigrantVisaTypes, getNonImmigrantVisaTypes } from "../../../utils/helper";

let requiredVisaDetails = true;

const AccountOwnerImmigrationInfo = ({ nextStep, prevStep, handleSubmit, active, dispatch, processing, secondary, investAccount }) => {
    const [countries] = useState(getCountries());
    const [showVisaFields, setShowVisaFields] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [disableImmigrationStatus, setDisableImmigrationStatus] = useState(false);
    const [visaTypes, setVisaTypes] = useState([]);

    useEffect(() => {
        if (active && investAccount && investAccount.id) {
            if (secondary) {
                let visa_expiration = investAccount.secondary_account_owner_us_visa_expiration || null;
                if(visa_expiration){
                    visa_expiration = moment(investAccount.secondary_account_owner_us_visa_expiration).format('MM/DD/YYYY');
                }
                dispatch(change('AccountOwnerImmigrationInfoForm', 'country_of_citizenship', investAccount.secondary_account_owner_country_of_citizenship || null));
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_immigration_status', investAccount.secondary_account_owner_us_immigration_status || null));
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_type', investAccount.secondary_account_owner_us_visa_type || null));
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_number', investAccount.secondary_account_owner_us_visa_number || null));
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_expiration', visa_expiration));
                if (investAccount.secondary_account_owner_country_of_citizenship) {
                    handleChangeCountry(investAccount.secondary_account_owner_country_of_citizenship);
                }
                if (investAccount.primary_account_owner_country_of_citizenship !== 'United States' && investAccount.secondary_account_owner_us_immigration_status) {
                    handleImmigrationStatusChange(investAccount.secondary_account_owner_us_immigration_status);
                }
            } else {
                let visa_expiration = investAccount.primary_account_owner_us_visa_expiration || null;
                if(visa_expiration){
                    visa_expiration = moment(investAccount.primary_account_owner_us_visa_expiration).format('MM/DD/YYYY');
                }
                dispatch(change('AccountOwnerImmigrationInfoForm', 'country_of_citizenship', investAccount.primary_account_owner_country_of_citizenship || null));
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_immigration_status', investAccount.primary_account_owner_us_immigration_status || null));
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_type', investAccount.primary_account_owner_us_visa_type || null));
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_number', investAccount.primary_account_owner_us_visa_number || null));
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_expiration', visa_expiration));
                if (investAccount.primary_account_owner_country_of_citizenship) {
                    handleChangeCountry(investAccount.primary_account_owner_country_of_citizenship);
                }
                if (investAccount.primary_account_owner_country_of_citizenship !== 'United States' && investAccount.primary_account_owner_us_immigration_status) {
                    handleImmigrationStatusChange(investAccount.primary_account_owner_us_immigration_status);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, investAccount]);

    useEffect(() => {
        setShowVisaFields(true);
        setDisableImmigrationStatus(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondary]);

    const availableImmigrationStatus = [
        {
            label: 'US Citizen',
            value: 'US Citizen'
        },
        {
            label: 'Green Card Holder',
            value: 'Green Card Holder'
        },
        {
            label: 'Immigrant Visa',
            value: 'Immigrant Visa'
        },
        {
            label: 'Non-Immigrant Visa',
            value: 'Non-Immigrant Visa'
        }
    ];

    const handleChangeCountry = value => {
        setSelectedCountry(value);
        if (value === 'United States') {
            dispatch(change('AccountOwnerImmigrationInfoForm', 'us_immigration_status', 'US Citizen'));
            dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_type', null));
            dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_number', null));
            dispatch(change('AccountOwnerImmigrationInfoForm', 'us_visa_expiration', null));
            setDisableImmigrationStatus(true);
            requiredVisaDetails = false;
        } else {
            setDisableImmigrationStatus(false);
            requiredVisaDetails = true;
        }
        setShowVisaFields(requiredVisaDetails)
    };

    const handleImmigrationStatusChange = value => {
        if (value === 'Green Card Holder') {
            requiredVisaDetails = value !== 'Green Card Holder';
            setShowVisaFields(requiredVisaDetails);
            return;
        }

        if (selectedCountry === 'United States' && value === 'US Citizen') {
            requiredVisaDetails = false;
        } else {
            requiredVisaDetails = true;
        }
        setShowVisaFields(requiredVisaDetails);
        if (requiredVisaDetails) {
            if (value === 'Immigrant Visa') {
                setVisaTypes(getImmigrantVisaTypes());
                dispatch(change('AccountOwnerImmigrationInfoForm', 'us_immigration_status', investAccount.primary_account_owner_us_immigration_status || null));
            } else if (value === 'Non-Immigrant Visa') {
                setVisaTypes(getNonImmigrantVisaTypes());
            }
        }
    };

    const handleNext = (formData) => {
        let payload = {};
        if (secondary) {
            payload.secondary_account_owner_country_of_citizenship = formData.country_of_citizenship;
            payload.secondary_account_owner_us_immigration_status = formData.us_immigration_status;
            payload.secondary_account_owner_us_visa_type = formData.us_visa_type;
            payload.secondary_account_owner_us_visa_number = formData.us_visa_number;
            payload.secondary_account_owner_us_visa_expiration = formData.us_visa_expiration;
        } else {
            payload.primary_account_owner_country_of_citizenship = formData.country_of_citizenship;
            payload.primary_account_owner_us_immigration_status = formData.us_immigration_status;
            payload.primary_account_owner_us_visa_type = formData.us_visa_type;
            payload.primary_account_owner_us_visa_number = formData.us_visa_number;
            payload.primary_account_owner_us_visa_expiration = formData.us_visa_expiration;
        }
        nextStep(
            payload,
            formData.us_immigration_status === 'US Citizen' ? 1 : 0
        );
    };

    const handleBack = () => {
        prevStep();
    }

    return (
        <div className={`account-setup-wrapper ${!active ? 'd-none' : ''}`}>
            <Form className="form-container" onSubmit={handleSubmit(handleNext)}>
                <div className="back-btn">
                    <Button color="link" onClick={handleBack}>back</Button>
                </div>
                <div className="form-header mt-md-3 mt-0">
                    <h2>{secondary ? 'SECONDARY' : 'PRIMARY'} ACCOUNT OWNER</h2>
                    <span>Please provide the Immigration Status of the account owner</span>
                </div>
                <div className="account-info-container">
                    <FormGroup row>
                        <Label sm={5}>Country of Citizenship</Label>
                        <Col sm={7}>
                            <Field
                                component={ValidatedSelect}
                                isSearchable={true}
                                isClearable={true}
                                onChange={handleChangeCountry}
                                options={countries}
                                name="country_of_citizenship"
                                placeholder="Select Country" />
                        </Col>
                    </FormGroup>
                    <FormGroup row>
                        <Label sm={5}>US Immigration Status</Label>
                        <Col sm={7}>
                            <Field
                                component={ValidatedSelect}
                                disabled={disableImmigrationStatus}
                                options={availableImmigrationStatus}
                                onChange={handleImmigrationStatusChange}
                                name="us_immigration_status"
                                placeholder="Select Immigration Status" />
                        </Col>
                    </FormGroup>
                    {showVisaFields && (
                        <React.Fragment>
                            <FormGroup row>
                                <Label sm={5}>US Visa Type</Label>
                                <Col sm={7}>
                                    <Field
                                        component={ValidatedSelect}
                                        options={visaTypes}
                                        isSearchable={true}
                                        isClearable={true}
                                        disabled={!visaTypes.length}
                                        name="us_visa_type"
                                        placeholder="Select Visa Type" />
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label sm={5}>US Visa Number</Label>
                                <Col sm={7}>
                                    <Field
                                        component={ValidatedInput}
                                        type="text"
                                        name="us_visa_number"
                                        placeholder="US Visa Number" />
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label sm={5}>US Visa Expiration</Label>
                                <Col sm={7}>
                                    <Field
                                        component={ValidatedInput}
                                        type="date"
                                        minDate={new Date()}
                                        name="us_visa_expiration"
                                        placeholder="US Visa Expiration" />
                                </Col>
                            </FormGroup>
                        </React.Fragment>
                    )}
                </div>
                <div className="button_wrapper">
                    <button className="custom-btn-primary" type="submit" disabled={processing}>
                        NEXT STEP {processing && <FontAwesomeIcon icon={faSpinner} spin />}
                    </button>
                </div>
            </Form>
        </div>
    )
};

const validate = (values) => {
    const errors = {};
    if (!values.country_of_citizenship) {
        errors.country_of_citizenship = 'Field is required';
    }
    if (!values.us_immigration_status) {
        errors.us_immigration_status = 'Field is required';
    }
    if (requiredVisaDetails && !values.us_visa_type) {
        errors.us_visa_type = 'Field is required';
    }
    if (requiredVisaDetails && !values.us_visa_number) {
        errors.us_visa_number = 'Field is required';
    }
    if (requiredVisaDetails && !values.us_visa_expiration) {
        errors.us_visa_expiration = 'Field is required';
    }
    return errors;
};

export default reduxForm({
    form: 'AccountOwnerImmigrationInfoForm',
    validate,
})(AccountOwnerImmigrationInfo);
